import { Apple } from "GameObjects/StoryGame/ch5/Apple";
import { Horse } from "GameObjects/StoryGame/ch5/Horse";
import { HorseItem } from "GameObjects/StoryGame/ch5/HorseItem";
import { HorseTarget } from "GameObjects/StoryGame/ch5/HorseTarget";
import { SceneDraggable } from "GameObjects/StoryGame/SceneDraggable";
import { SceneSpine } from "GameObjects/StoryGame/SceneSpine";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Expo, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list } from "Utils/Reflection";

import {ch5_p04} from "../../../layouts/ch5_p04/ch5_p04";

export class Chapter5Page4 extends AbstractStoryState {

    readonly panContainer = this.addChild(new PIXI.Container());

    readonly bg = this.panContainer.addChild(new SceneSprite());
    readonly blanket = this.panContainer.addChild(new SceneSprite());
    readonly hollyBack = this.panContainer.addChild(new SceneSprite());
    readonly horse = this.panContainer.addChild(new Horse());
    readonly hollyFront = this.panContainer.addChild(new SceneSprite());
    readonly apple = this.panContainer.addChild(new Apple());
    readonly brush = this.panContainer.addChild(new HorseItem());
    readonly rope = this.panContainer.addChild(new SceneSprite());
    readonly saddle = this.panContainer.addChild(new HorseItem());

    readonly appleTarget = this.panContainer.addChild(new HorseTarget(this.apple));
    readonly hairBrushTarget = this.panContainer.addChild(new HorseTarget(this.brush));
    readonly tailBrushTarget = this.panContainer.addChild(new HorseTarget(this.brush));
    readonly saddleTarget = this.panContainer.addChild(new HorseTarget(this.saddle));

	readonly frameLeft = this.addChild(SceneSprite.withPrefix("ch5_p04/"));
    readonly frameRight = this.addChild(SceneSprite.withPrefix("ch5_p04/"));

    protected horseIdleTimeline?: TimelineLite;
    protected chewTimeline?: TimelineLite;
    protected brushTimeline?: TimelineLite;
    private _blanketPos?: PIXI.Point;

    public init(pSize: PIXI.Point, pData: any) {
		// exported layouts from Animate CC
        ch5_p04.call(this);

        this.frameLeft.visible = false; // temporary?
        this.frameRight.visible = false; // temporary?

		// super.init calls onResize
        super.init(pSize, pData);

        this.apple.interactive = false;
        this.brush.interactive = false;
        this.saddle.interactive = false;
        this.blanket.interactive = false;

        this.appleTarget.isActive = false;
        this.hairBrushTarget.isActive = false;
        this.tailBrushTarget.isActive = false;
        this.saddleTarget.isActive = false;

        this.horse.scale.set(1 / 3); // override scale from .fla
        this.horse.SmallIdle();
        this.horse.update(0);

        this._blanketPos = this.blanket.position.clone();

        this._subscriptions.push([
            // callbacks from horse
            // TODO: constants instead of strings
            PubSub.subscribe('drop', this.onItemDropped.bind(this)),
            PubSub.subscribe('hover', this.onItemHover.bind(this)),
            PubSub.subscribe('hoverend', this.onItemHoverEnd.bind(this)),
            PubSub.subscribe('hovermove', this.onItemHoverMove.bind(this)),
            // callbacks from items
            PubSub.subscribe(HLF.DRAG_BEGIN, this.onDragBegin.bind(this)),
            PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onDragBegin.bind(this)),
            PubSub.subscribe(HLF.DRAG_END, this.onDragEnd.bind(this)),
            PubSub.subscribe(HLF.DRAGGABLE_DESELECTED, this.onDragEnd.bind(this)),
        ]);
    }

	public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.horse.update(pDeltaTime);
	}

	public animateIn(pOnComplete: ()=>void) {
        super.animateIn(pOnComplete);
    }

    public animateOut(pOnComplete: ()=>void) {
        super.animateOut(pOnComplete);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();

        const onHorseClicked = ()=>{
            new TimelineLite()
                .to(this.panContainer, 2, {pixi: {x: -125}}, "+=0");
            ;
            this.hollyFront.FadeOut();
            this.hollyBack.FadeOut();

            this.horse.removeListener(HLF.Input.Events.POINTER_TAP, onHorseClicked);
            this.horse.buttonMode = false;

            this.apple.interactive = true;
            this.brush.interactive = true;
            this.saddle.interactive = true;

            this.horseIdleTimeline = new TimelineLite();
            this.horseIdleTimeline.call(()=>{
                this.horse.Idle();
                this.horseIdleTimeline?.delay(HLF.Random.floatBetween(5, 10));
                this.horseIdleTimeline?.restart(true);
            });
            this.horseIdleTimeline.delay(HLF.Random.floatBetween(5, 10));

            // this.showHint(this.apple, "pulse");
        }
        this.horse.interactive = true;
        this.horse.buttonMode = true;
        this.horse.accessible = true;
        this.horse.addListener(HLF.Input.Events.POINTER_TAP, onHorseClicked);

        // this.showHint({x: 832, y: 384}, "hand", 8);
    }

    public destroy() {
        super.destroy();
        this.horseIdleTimeline?.kill();
        this.chewTimeline?.kill();
        this.brushTimeline?.kill();
    }

    // called *before* onDragEnd
    protected onItemDropped(pTopic: string, pData: {item: HorseItem, target: HorseTarget}): void {
        console.log(pTopic, pData);

        // PubSub.publishSync(HIDE_ALL_HINTS, undefined);
        // hide item
        pData.item.deselect();
        pData.item.visible = false;
        pData.item.interactive = false;

        if (pData.item === this.saddle) {
            this.horse.SaddleOn();
            this.horse.Idle();
            this.blanket.visible = false;
            this.rope.visible = false;
            const saddleSound = "HH_Ch5_Horse_Saddle_P5_FX";
            HLF.Application.instance.audio.play(saddleSound, 1, false, HLF.AudioCategory.SFX);
        }
        else if (pData.item === this.apple) {
            const chewSound = "HH_Ch5_Horse_Chomp_Apple_P5_FX";
            const sound = HLF.Application.instance.audio.getAudioTrack(chewSound, HLF.AudioCategory.SFX);
            if (!!sound && !sound.isPlaying) {
                sound.play();
            }
            this.horse.Chew();
            TweenLite.delayedCall(1, ()=>{
                sound?.stop();
                this.horse.StopChewing();
            });
        }
        else if (pData.item === this.brush) {
            //
        }

        if (!this.saddle.visible && !this.apple.visible && !this.brush.visible) { // TEMPORARY
            TweenLite.delayedCall(1, ()=>{
                this.gotoNextState({transition: "left"});
            });
        }
    }

    // called *after* onItemDropped
    protected onDragEnd(pTopic: string, pData: HorseItem): void {
        // console.log(pTopic, pData);

        // if drag ended without dropping on horse, or item deselected
        if (pData && pData.interactive) {
            // reset button position and scale
            pData.visuals.position.set(0);
            pData.visuals.scale.set(1);

            switch (pData) {
                case this.saddle:
                    this.rope.visible = true;
                    HLF.PixiUtils.setParent(this.blanket, this.panContainer);
                    this.blanket.position.copyFrom(this._blanketPos!);
                    this.blanket.scale.set(0.5);
                    this.panContainer.setChildIndex(this.blanket, this.panContainer.getChildIndex(this.saddle) - 1);
                    break;
            }
        }
    }

    protected onDragBegin(pTopic: string, pData: HorseItem): void {
        // console.log(pTopic, pData);

        switch (pData) {
            case this.saddle:
                this.rope.visible = false;
                HLF.PixiUtils.setParent(this.blanket, this.saddle.visuals);
                this.saddle.visuals.setChildIndex(this.blanket, 0);
                this.blanket.scale.set(1);
                this.blanket.position.set(0, 0);
                break;
        }

        // hide hint on item
        this.hideHint(pData);
    }

     protected onItemHover(pTopic: string, pData: {item: HorseItem, target: HorseTarget}): void {
        // console.log(pTopic, pData);

        if (pData.item === this.saddle) {
            // this.horse.SaddleOn();
        }
        else if (pData.item === this.apple) {
            this.horse.Chew();

            console.log('chewing for 3 seconds');

            const chewSound = "HH_Ch5_Horse_Chomp_Apple_P5_FX";
            HLF.Application.instance.audio.play(chewSound, 1, true, HLF.AudioCategory.SFX);

            if (this.chewTimeline !== undefined) {
                this.chewTimeline.restart(true);
            }
            else {
                this.chewTimeline = new TimelineLite();
                this.chewTimeline.call(()=>{
                    this.apple.Eat(0.5);
                });
                this.chewTimeline.call(()=>{
                    this.apple.Eat(1);
                }, undefined, undefined, "+=1.5");
                this.chewTimeline.call(()=>{
                    this.apple.deselect();
                    this.appleTarget.consume(this.apple);
                }, undefined, undefined, "+=1.5");
                this.chewTimeline.call(()=>{
                    console.log('done chewing');
                    HLF.Application.instance.audio.stop(chewSound, HLF.AudioCategory.SFX);
                    this.horse.StopChewing();
                    this.horse.Idle();
                }, undefined, undefined, "+=1");
            }
        }
        if (pData.item === this.brush) {

            if (pData.target === this.hairBrushTarget) {
                // this.horse.BrushHair(0);
            }
            else if (pData.target === this.tailBrushTarget) {
                // this.horse.BrushTail(0);
            }

            const brushSound = "HH_Ch5_Horse_Brush_P5_FX";
            HLF.Application.instance.audio.play(brushSound, 1, true, HLF.AudioCategory.SFX);

            if (this.brushTimeline !== undefined) {
                this.brushTimeline.resume();
            }
            else {
                this.brushTimeline = new TimelineLite();
                this.brushTimeline.call(()=>{
                    console.log('done brushing');
                    this.horse.StopBrushing();

                    pData.target.consume(this.brush);
                    this.brush.deselect();
                    this.brush.drop();
                    this.hairBrushTarget.isActive = false;
                    this.tailBrushTarget.isActive = false;

                    HLF.Application.instance.audio.stop(brushSound, HLF.AudioCategory.SFX);

                    this.horse.Idle();
                }).delay(3);
            }
        }
    }

    protected onItemHoverEnd(pTopic: string, pData: {item: HorseItem, target: HorseTarget}): void {
        // console.log(pTopic, pData);
        if (pData.item === this.saddle) {
            // this.horse.SaddleOn();
        }
        else if (pData.item === this.apple) {
            this.horse.StopChewing();
            this.chewTimeline?.pause();
            this.apple.Eat(0);
            const chewSound = "HH_Ch5_Horse_Chomp_Apple_P5_FX";
            HLF.Application.instance.audio.getAudioTrack(chewSound, HLF.AudioCategory.SFX)?.stop();
        }
        if (pData.item === this.brush) {
            this.horse.StopBrushing();
            this.brushTimeline?.pause();
            const brushSound = "HH_Ch5_Horse_Brush_P5_FX";
            HLF.Application.instance.audio.getAudioTrack(brushSound, HLF.AudioCategory.SFX)?.stop();
        }

        pData.item.visuals.position.set(0);
    }

    protected onItemHoverMove(pTopic: string, pData: {item: HorseItem, target: HorseTarget, yPercent: number}): void {
        // console.log(pTopic, pData);
        if (pData.item === this.brush) {
            // this.brushTimeline?.progress(pData.yPercent);
            if (pData.target === this.tailBrushTarget) {
                this.horse.BrushTail(pData.yPercent);
            }
            else if (pData.target === this.hairBrushTarget) {
                this.horse.BrushHair(pData.yPercent);
            }
        }
    }
}